import classNames from "classnames";
import {MutableRefObject} from "react";
import {SubmitHandler, useFormContext} from "react-hook-form";

import {useCurrentUserContext} from "@/components/context/hooks.ts";
import {Divider} from "@/components/divider/Divider.tsx";
// @ts-expect-error no ts support yet
import TransactionScriptEditor from "@/legacy/transaction/TransactionScriptEditor.jsx";
import {ServiceField} from "@/lib/constants/service.ts";
import {
  LegacyURL,
  MonitoringServiceChoices,
  MonitoringServiceType,
  ServiceDefinition,
  StepDefinitions,
} from "@/lib/types/checks.ts";
import {CheckVaultServiceField} from "@/pages/checks/checkVaultServiceVariables/CheckVaultServiceField.tsx";
import {CheckFormAutoGeneratedField} from "@/pages/checks/components/form/fields/CheckFormAutoGeneratedField.tsx";
import {TransactionScriptEditorField} from "@/pages/checks/components/form/fields/TransactionScriptEditorField.tsx";
import {FIELD_TYPES} from "@/pages/checks/constants.ts";
import {CreateCheckFormValues} from "@/pages/checks/update/CreateCheckForm.types.ts";
import {hasVault} from "@/pages/checks/utils.ts";

import styles from "./CheckDetailsForm.module.scss";

interface CheckStepsFormProps {
  service: ServiceDefinition;
  serviceType: MonitoringServiceType;
  steps: StepDefinitions;
  mspChoices: MonitoringServiceChoices;
  legacyURL: LegacyURL;
  transactionEditorRef: MutableRefObject<TransactionScriptEditor>;
  handleSave: SubmitHandler<CreateCheckFormValues>;
  testRunnerForm?: null | JSX.Element;
}

export const CheckStepsForm = ({
  service,
  serviceType,
  steps,
  mspChoices,
  legacyURL,
  transactionEditorRef,
  handleSave,
  testRunnerForm,
}: CheckStepsFormProps) => {
  const {brand} = useCurrentUserContext();
  const {handleSubmit} = useFormContext();
  const onOpenRecorder = () => {
    handleSubmit(async (d) => {
      // @ts-expect-error handler type mismatch
      const id = await handleSave(d);
      if (id) {
        // @ts-expect-error switch to transaction recorder
        window.location = `${brand.siteURL}${legacyURL.transactionRecorderURL}?service=${id}`;
      }
    })();
  };
  const serviceFieldsMap = service.fields.reduce(
    (acc, field) => {
      acc[field.field as ServiceField] = field;
      return acc;
    },
    {} as Record<ServiceField, (typeof service.fields)[number]>
  );

  return (
    <div className="d-flex gap-4">
      <div className="pulseMaxWidthL w-100">
        <TransactionScriptEditorField
          txnRef={transactionEditorRef}
          svc={service}
          stepsDef={steps}
          pagespeedReportURL={
            serviceType === MonitoringServiceType.PAGESPEED
              ? `${brand.siteURL}${legacyURL.pagespeedReportURL}`
              : undefined
          }
          onOpenRecorder={serviceType === MonitoringServiceType.TRANSACTION ? onOpenRecorder : undefined}
        />
        {hasVault(serviceType) && <CheckVaultServiceField className="mt-5" />}
        {serviceType === MonitoringServiceType.PAGESPEED && (
          <>
            <div className="pulseMaxWidthL mt-5">
              <CheckFormAutoGeneratedField
                field={serviceFieldsMap[ServiceField.HEADERS]}
                versions={service.versions}
                serviceType={service.service_type}
                type={FIELD_TYPES[ServiceField.HEADERS]}
                mspChoices={mspChoices}
              />
            </div>
            <div className="mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row pulseMaxWidthL">
              <div className="pulseMaxWidthS flex-grow-1 smBreakpointPulseMaxWidthXL">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.PAGESPEED_EMULATED_DEVICES]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.PAGESPEED_EMULATED_DEVICES]}
                  mspChoices={mspChoices}
                />
              </div>
              <div className="pulseMaxWidthS flex-grow-1 smBreakpointPulseMaxWidthXL">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.PAGESPEED_CONNECTION_THROTTLING]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.PAGESPEED_CONNECTION_THROTTLING]}
                  mspChoices={mspChoices}
                />
              </div>
            </div>
            <div className="pulseMaxWidthS mt-4 smBreakpointPulseMaxWidthXL">
              <CheckFormAutoGeneratedField
                field={serviceFieldsMap[ServiceField.PAGESPEED_GRADE_THRESHOLD]}
                versions={service.versions}
                serviceType={service.service_type}
                type={FIELD_TYPES[ServiceField.PAGESPEED_GRADE_THRESHOLD]}
                mspChoices={mspChoices}
              />
            </div>
            <div className="pulseTextH3Bold mt-5">Credentials</div>
            <div className="mt-4 d-flex gap-4 gap-sm-3 flex-column flex-sm-row pulseMaxWidthL">
              <div className="pulseMaxWidthM flex-grow-1 smBreakpointPulseMaxWidthXL">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.USERNAME]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.USERNAME]}
                  mspChoices={mspChoices}
                />
              </div>
              <div className="pulseMaxWidthM flex-grow-1 smBreakpointPulseMaxWidthXL">
                <CheckFormAutoGeneratedField
                  field={serviceFieldsMap[ServiceField.PASSWORD]}
                  versions={service.versions}
                  serviceType={service.service_type}
                  type={FIELD_TYPES[ServiceField.PASSWORD]}
                  mspChoices={mspChoices}
                />
              </div>
            </div>
            <div className="pulseMaxWidthL mt-4">
              <CheckFormAutoGeneratedField
                field={serviceFieldsMap[ServiceField.PAGESPEED_EXCLUDE_URLS]}
                versions={service.versions}
                serviceType={service.service_type}
                type={FIELD_TYPES[ServiceField.PAGESPEED_EXCLUDE_URLS]}
                mspChoices={mspChoices}
              />
            </div>
          </>
        )}
        {testRunnerForm && <div className={`d-md-none mt-4`}>{testRunnerForm}</div>}
      </div>

      {testRunnerForm && (
        <>
          <div className="d-none d-md-block">
            <Divider orientation="vertical" className="mx-2" />
          </div>
          <div className={classNames(styles.testRunner, "d-none d-md-block")}>{testRunnerForm}</div>
        </>
      )}
    </div>
  );
};
